<template>
  <div class="admin-login-container">
    <div class="box">
      <!-- Left Side - Image & Branding -->
      <div class="left-side">
        <div class="overlay"></div>
        <div class="brand-content">
          <!-- Logo -->
          <div class="logo-section">
            <div class="logo-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                ></path>
              </svg>
            </div>
            <h1 class="brand-title">Store Thanh Danh</h1>
            <p class="brand-subtitle">Hệ thống quản trị</p>
          </div>

          <!-- Features -->
          <div class="features-list">
            <div class="feature-item">
              <div class="feature-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
              </div>
              <div class="feature-content">
                <h3>Bảo mật cao</h3>
                <p>Xác thực đa lớp với mã OTP</p>
              </div>
            </div>

            <div class="feature-item">
              <div class="feature-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  ></path>
                </svg>
              </div>
              <div class="feature-content">
                <h3>Quản lý hiệu quả</h3>
                <p>Giao diện thân thiện, dễ sử dụng</p>
              </div>
            </div>

            <div class="feature-item">
              <div class="feature-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
              </div>
              <div class="feature-content">
                <h3>Hỗ trợ 24/7</h3>
                <p>Đội ngũ hỗ trợ luôn sẵn sàng</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side - Login Form -->
      <div class="right-side">
        <div class="form-wrapper">
          <!-- Login Header -->
          <div class="login-header">
            <div class="user-icon-wrapper">
              <svg
                class="user-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                ></path>
              </svg>
            </div>
            <h2 class="login-title">
              {{ isRegisterMode ? "Đăng ký" : "Đăng nhập" }}
            </h2>
            <p class="login-subtitle">
              {{
                isRegisterMode
                  ? "Tạo tài khoản quản trị viên"
                  : "Tài khoản quản trị viên"
              }}
            </p>
          </div>

          <!-- Error Message -->
          <div v-if="errors.message" class="alert-error">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <span>{{ errors.message[0] }}</span>
          </div>

          <!-- Login Method Toggle -->
          <div v-if="!isRegisterMode" class="method-toggle">
            <button
              @click="switchLoginMethod('password')"
              :class="['method-btn', { active: loginMethods === 'password' }]"
            >
              Mật khẩu
            </button>
            <button
              @click="switchLoginMethod('otp')"
              :class="['method-btn', { active: loginMethods === 'otp' }]"
            >
              Mã OTP
            </button>
          </div>

          <transition name="fade" mode="out-in">
            <!-- Login Form -->
            <div v-if="!isRegisterMode" key="login" class="form-container">
              <div class="form-group">
                <label class="form-label">Email</label>
                <div class="input-wrapper">
                  <svg
                    class="input-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    ></path>
                  </svg>
                  <input
                    type="email"
                    placeholder="Nhập email của bạn..."
                    class="form-input"
                    v-model="form.email"
                    @blur="validateEmail"
                  />
                </div>
                <span v-if="errors.email" class="error-message">{{
                  errors.email[0]
                }}</span>
              </div>

              <!-- Password Field -->
              <div v-if="loginMethods === 'password'" class="form-group">
                <label class="form-label">Mật khẩu</label>
                <div class="input-wrapper">
                  <svg
                    class="input-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    ></path>
                  </svg>
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    placeholder="Nhập mật khẩu"
                    class="form-input password-input"
                    v-model="form.password"
                  />
                  <button
                    type="button"
                    @click="showPassword = !showPassword"
                    class="password-toggle"
                  >
                    <svg
                      v-if="!showPassword"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      ></path>
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      ></path>
                    </svg>
                    <svg
                      v-else
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      ></path>
                    </svg>
                  </button>
                </div>
                <span v-if="errors.password" class="error-message">{{
                  errors.password[0]
                }}</span>
              </div>

              <!-- OTP Field -->
              <div v-if="loginMethods === 'otp'" class="form-group">
                <label class="form-label">Mã OTP</label>
                <div class="otp-container">
                  <input
                    type="text"
                    placeholder="Nhập mã OTP"
                    class="otp-input"
                    v-model="form.otp"
                    maxlength="6"
                  />
                  <button
                    type="button"
                    @click="handleSendOTP"
                    class="otp-button"
                    :disabled="!reSendOtp || loading"
                    :class="{ disabled: !reSendOtp }"
                  >
                    <span v-if="loading" class="spinner"></span>
                    <span v-else>{{ otpButtonText }}</span>
                  </button>
                </div>
                <span v-if="errors.otp" class="error-message">{{
                  errors.otp[0]
                }}</span>
                <p v-if="otpSent && !reSendOtp" class="info-message">
                  {{ formattedCountdown }}
                </p>
              </div>

              <!-- Remember Me & Forgot Password -->
              <div class="remember-section">
                <label class="remember-checkbox">
                  <input
                    type="checkbox"
                    class="checkbox"
                    v-model="rememberMe"
                  />
                  <span class="checkbox-label">Ghi nhớ đăng nhập</span>
                </label>
                <a href="#" class="forgot-link">Quên mật khẩu?</a>
              </div>

              <!-- Submit Button -->
              <button
                @click="handleLogin"
                class="submit-btn"
                :disabled="loading"
                :class="{ loading: loading }"
              >
                <span v-if="loading" class="spinner"></span>
                <span v-else>Đăng Nhập</span>
              </button>
            </div>

            <!-- Register Form -->
            <div v-else key="register" class="form-container">
              <!-- Full Name -->
              <div class="form-group">
                <label class="form-label"
                  >Họ và tên <span class="required">*</span></label
                >
                <div class="input-wrapper">
                  <svg
                    class="input-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    ></path>
                  </svg>
                  <input
                    v-model="registerForm.name"
                    type="text"
                    placeholder="Nhập họ và tên"
                    class="form-input"
                    @blur="validateField('name')"
                  />
                </div>
                <span v-if="errors.name" class="error-message">
                  {{ errors.name[0] }}
                </span>
              </div>

              <!-- Phone Number Field -->
              <div v-if="registerMethod === 'phone'" class="form-group">
                <label class="form-label"
                  >Số điện thoại <span class="required">*</span></label
                >
                <div class="input-wrapper">
                  <svg
                    class="input-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    ></path>
                  </svg>
                  <input
                    v-model="registerForm.phone"
                    type="tel"
                    placeholder="Nhập số điện thoại"
                    class="form-input"
                    @blur="validateField('phone')"
                  />
                </div>
                <span v-if="errors.phone" class="error-message">
                  {{ errors.phone[0] }}
                </span>
              </div>

              <!-- Email Field -->
              <div v-if="registerMethod === 'email'" class="form-group">
                <label class="form-label"
                  >Email <span class="required">*</span></label
                >
                <div class="input-wrapper">
                  <svg
                    class="input-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    ></path>
                  </svg>
                  <input
                    v-model="registerForm.email"
                    type="email"
                    placeholder="Nhập địa chỉ email"
                    class="form-input"
                    @blur="validateField('email')"
                  />
                </div>
                <span v-if="errors.email" class="error-message">
                  {{ errors.email[0] }}
                </span>
              </div>

              <!-- OTP Verification -->
              <div v-if="showOtpVerification" class="form-group">
                <label class="form-label"
                  >Mã xác thực OTP <span class="required">*</span></label
                >
                <div class="otp-container">
                  <input
                    v-model="registerForm.otp"
                    type="text"
                    maxlength="6"
                    placeholder="Nhập mã OTP"
                    class="otp-input"
                  />
                  <button
                    type="button"
                    @click="sendRegisterOTP"
                    :disabled="!canResendOTP || loading"
                    class="otp-button"
                    :class="{ disabled: !canResendOTP }"
                  >
                    <span v-if="loadingOTP" class="spinner"></span>
                    <span v-else>{{ otpButtonText }}</span>
                  </button>
                </div>
                <span v-if="errors.otp" class="error-message">
                  {{ errors.otp[0] }}
                </span>
                <p v-if="otpSent && !canResendOTP" class="info-message">
                  {{ formattedCountdown }}
                </p>
              </div>

              <!-- Password Field -->
              <div class="form-group">
                <label class="form-label"
                  >Mật khẩu <span class="required">*</span></label
                >
                <div class="input-wrapper">
                  <svg
                    class="input-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    ></path>
                  </svg>
                  <input
                    v-model="registerForm.password"
                    :type="showPassword ? 'text' : 'password'"
                    placeholder="Nhập mật khẩu (tối thiểu 6 ký tự)"
                    class="form-input password-input"
                    @blur="validateField('password')"
                  />
                  <button
                    type="button"
                    @click="showPassword = !showPassword"
                    class="password-toggle"
                  >
                    <svg
                      v-if="!showPassword"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      ></path>
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      ></path>
                    </svg>
                    <svg
                      v-else
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      ></path>
                    </svg>
                  </button>
                </div>
                <span v-if="errors.password" class="error-message">
                  {{ errors.password[0] }}
                </span>
                <!-- Password Strength Indicator -->
                <div v-if="registerForm.password" class="password-strength">
                  <div
                    class="strength-bar"
                    :class="passwordStrength.class"
                  ></div>
                  <span class="strength-text">{{ passwordStrength.text }}</span>
                </div>
              </div>

              <!-- Confirm Password Field -->
              <div class="form-group">
                <label class="form-label"
                  >Xác nhận mật khẩu <span class="required">*</span></label
                >
                <div class="input-wrapper">
                  <svg
                    class="input-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  <input
                    v-model="registerForm.password_confirmation"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    placeholder="Nhập lại mật khẩu"
                    class="form-input password-input"
                    @blur="validateField('password_confirmation')"
                  />
                  <button
                    type="button"
                    @click="showConfirmPassword = !showConfirmPassword"
                    class="password-toggle"
                  >
                    <svg
                      v-if="!showConfirmPassword"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      ></path>
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      ></path>
                    </svg>
                    <svg
                      v-else
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      ></path>
                    </svg>
                  </button>
                </div>
                <span v-if="errors.password_confirmation" class="error-message">
                  {{ errors.password_confirmation[0] }}
                </span>
              </div>

              <!-- Terms and Conditions -->
              <div class="form-group">
                <label class="remember-checkbox">
                  <input
                    v-model="registerForm.agree_terms"
                    type="checkbox"
                    class="checkbox"
                  />
                  <span class="checkbox-label">
                    Tôi đồng ý với
                    <a href="#" class="link-text">Điều khoản sử dụng</a> và
                    <a href="#" class="link-text">Chính sách bảo mật</a>
                  </span>
                </label>
                <span v-if="errors.agree_terms" class="error-message">
                  {{ errors.agree_terms[0] }}
                </span>
              </div>

              <!-- Submit Button -->
              <button
                @click="handleRegister"
                :disabled="loading || !registerForm.agree_terms"
                class="submit-btn"
                :class="{
                  loading: loading,
                  disabled: !registerForm.agree_terms,
                }"
              >
                <span v-if="loading" class="spinner"></span>
                <span v-else>Đăng Ký</span>
              </button>
            </div>
          </transition>

          <!-- Divider -->
          <div class="divider">
            <div class="divider-line"></div>
            <span class="divider-text">Hoặc</span>
            <div class="divider-line"></div>
          </div>

          <!-- Register Link -->
          <div class="register-section">
            <p class="register-text">
              {{ isRegisterMode ? "Đã có tài khoản?" : "Chưa có tài khoản?" }}
              <a href="#" @click.prevent="toggleMode" class="register-link">
                {{ isRegisterMode ? "Đăng nhập ngay" : "Đăng ký ngay" }}</a
              >
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuth } from "@/composables/useAuth";
import { useToast } from "@/composables/useToast";
import { adminApi } from "@/services/api";

export default {
  name: "AdminLogin",
  setup() {
    const {
      login: authLogin,
      isAuthenticated,
      sendOTP,
      loginWithOTP: authLoginOTP,
    } = useAuth();
    const toast = useToast();
    return { authLogin, isAuthenticated, toast, sendOTP, authLoginOTP };
  },
  data() {
    return {
      loginMethods: "password",
      isRegisterMode: false,
      form: {
        email: "",
        password: "",
        otp: "",
      },
      registerForm: {
        name: "",
        phone: "",
        email: "",
        password: "",
        password_confirmation: "",
        otp: "",
        agree_terms: false,
      },
      showPassword: false,
      rememberMe: false,
      loading: false,
      otpSent: false,
      countdown: 0,
      reSendOtp: true,
      errors: {},
      backgroundImage:
        "https://images.unsplash.com/photo-1557821552-17105176677c?w=1200",
      showOtpVerification: false,
      registerMethod: "phone",
      showConfirmPassword: false,
    };
  },

  computed: {
    canResendOTP() {
      return this.countdown === 0;
    },

    formattedCountdown() {
      return `Gửi lại sau ${this.countdown}s`;
    },

    otpButtonText() {
      if (!this.otpSent) return "Gửi mã";
      return this.reSendOtp ? "Gửi lại" : this.formattedCountdown;
    },

    passwordStrength() {
      const password = this.registerForm.password;
      if (!password) return { class: "", text: "" };

      let strength = 0;
      if (password.length >= 6) strength++;
      if (password.length >= 10) strength++;
      if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
      if (/\d/.test(password)) strength++;
      if (/[^a-zA-Z\d]/.test(password)) strength++;

      if (strength <= 2) {
        return { class: "weak", text: "Yếu" };
      } else if (strength <= 3) {
        return { class: "medium", text: "Trung bình" };
      } else {
        return { class: "strong", text: "Mạnh" };
      }
    },
  },

  methods: {
    toggleMode() {
      this.isRegisterMode = !this.isRegisterMode;
      this.errors = {};
      this.otpSent = false;
      this.countdown = 0;
      this.reSendOtp = true;
      this.showOtpVerification = false;
      this.form = {
        phone: "",
        password: "",
      };
      this.registerForm = {
        name: "",
        phone: "",
        email: "",
        password: "",
        password_confirmation: "",
        otp: "",
        agree_terms: false,
      };
    },
    switchLoginMethod(method) {
      this.loginMethods = method;
      this.errors = {};
      this.form.otp = "";
      this.form.password = "";
    },

    validatePhone() {
      const phoneRegex = /(84|0[3|5|7|8|9])+([0-9]{8})\b/;

      if (!this.form.phone) {
        this.errors.phone = ["Vui lòng nhập số điện thoại"];
        return false;
      }

      if (!phoneRegex.test(this.form.phone)) {
        this.errors.phone = ["Số điện thoại không hợp lệ"];
        return false;
      }

      this.errors.phone = null;
      return true;
    },
    async handleSendOTP() {
      if (!this.validateEmail()) return;

      this.loading = true;
      this.errors = {};
      this.form.otp = "";
      const payload = { email: this.form.email };
      try {
        const response = await this.sendOTP(payload);
        if (response.data.success) {
          this.otpSent = true;
          this.reSendOtp = false;
          this.startCountdown(60);
          this.toast.success(response.data.message);
        }
      } catch (error) {
        console.log("Error : ", error);
        this.errors = {};
        const errorResponse = error.response?.data;
        if(errorResponse?.email) this.errors.email = errorResponse.email;
        
        const errorMsg = errorResponse.message;
        // this.toast.error(errorMsg);
        this.errors.message = [errorMsg];
      } finally {
        this.loading = false;
      }
    },

    startCountdown(seconds) {
      this.countdown = seconds;

      const interval = setInterval(() => {
        this.countdown--;

        if (this.countdown <= 0) {
          clearInterval(interval);
          this.reSendOtp = true;
        }
      }, 1000);
    },

    async handleLogin() {
      this.loginMethods === "password"
        ? await this.loginWithPassword()
        : await this.loginWithOTP();
    },

    async loginWithOTP() {
      this.errors = {};
      if (!this.validateEmail()) return;
      if (!this.form.otp) {
        this.errors.otp = ["Vui lòng nhập mã OTP"];
        return;
      }
      const credentials = {
        email: this.form.email,
        otp: this.form.otp,
      };
      this.loading = true;
      try {
        await this.authLoginOTP(credentials);
      this.$router.push("/admin/dashboard.html");

      } catch (error) {
        const errorRes = error.response?.data;
        if (errorRes?.errors?.email) {
          this.errors.email = errorRes.errors.email;
        }
        const errorMsg = errorRes?.data;
        if (errorMsg?.type === "otp") {
          this.errors.otp = [errorMsg.message];
        }
      } finally {
        this.loading = false;
      }
    },
    async loginWithPassword() {
      this.errors = {};
      if (this.loginMethods === "password") {
        if (!this.validateEmail()) return;
        if (!this.form.password) {
          this.errors.password = ["Vui lòng nhập mật khẩu"];
          return;
        }
      }
      this.loading = true;

      try {
        const identity = this.form.email;
        const payload = {
          login: identity,
          password: this.form.password,
          otp: this.form.otp,
          method: this.loginMethods,
          device_name: navigator.userAgent,
        };

        const response = await adminApi.login(payload);
        const { user, token } = response.data.data;
        localStorage.setItem("admin_token", token);
        localStorage.setItem("admin_user", JSON.stringify(user));
        await this.authLogin(payload);
        this.$router.push("/admin/dashboard.html");
        this.$toast.success("Đăng nhập thành công!");
      } catch (error) {
        console.error("Login error:", error);
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          this.errors.message = [error.response.data.message];
        } else {
          this.errors.message = ["Có lỗi xảy ra khi đăng nhập"];
        }
      } finally {
        this.loading = false;
      }
    },
    validateField(field) {
      switch (field) {
        case "name":
          if (!this.registerForm.name || this.registerForm.name.length < 2) {
            this.errors.name = ["Họ tên phải có ít nhất 2 ký tự"];
          } else {
            this.errors.name = null;
          }
          break;
        case "phone":
          this.validatePhone();
          break;
        case "email":
          this.validateEmail();
          break;
        case "password":
          if (
            !this.registerForm.password ||
            this.registerForm.password.length < 6
          ) {
            this.errors.password = ["Mật khẩu phải có ít nhất 6 ký tự"];
          } else {
            this.errors.password = null;
          }
          break;
        case "password_confirmation":
          if (
            this.registerForm.password !==
            this.registerForm.password_confirmation
          ) {
            this.errors.password_confirmation = [
              "Mật khẩu xác nhận không khớp",
            ];
          } else {
            this.errors.password_confirmation = null;
          }
          break;
      }
    },
    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      const emailToValidate = this.isRegisterMode
        ? this.registerForm.email
        : this.form.email;
      if (!emailToValidate) {
        this.errors.email = ["Vui lòng nhập email"];
        return false;
      }
      if (!emailRegex.test(emailToValidate)) {
        this.errors.email = ["Email không hợp lệ"];
        return false;
      }

      this.errors.email = null;
      return true;
    },
  },
};
</script>

<style scoped>
/* Container */
.admin-login-container {
  display: flex;
  min-height: 100vh;
  background: linear-gradient(135deg, #111111, #3a3a3a, #f36c21, #ffd54f);
  align-items: center;
}
.box {
  display: flex;
  margin: 0 auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  overflow: hidden;
}
/* Required field indicator */
.required {
  color: #ef4444;
  margin-left: 2px;
}
/* Left Side - Image */
.left-side {
  display: none;
  position: relative;
  background: linear-gradient(135deg, #ffb703, #fb8500, #f94144);
  overflow: hidden;
}

@media (min-width: 1024px) {
  .left-side {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
  }
}

.overlay {
  position: absolute;
  inset: 0;
  background: url("https://images.unsplash.com/photo-1557821552-17105176677c?w=1200")
    center/cover;
  opacity: 0.15;
}

.overlay::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.9) 0%,
    rgba(118, 75, 162, 0.9) 100%
  );
}

.brand-content {
  position: relative;
  z-index: 10;
  padding: 3rem;
  color: white;
  max-width: 500px;
}

/* Logo Section */
.logo-section {
  text-align: center;
  margin-bottom: 4rem;
}

.logo-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.logo-icon svg {
  width: 40px;
  height: 40px;
  stroke-width: 2;
}

.brand-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.brand-subtitle {
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.9);
}

/* Features List */
.features-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.feature-item {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s;
}

.feature-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(10px);
}

.feature-icon {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feature-icon svg {
  width: 24px;
  height: 24px;
}

.feature-content h3 {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.feature-content p {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
}

/* Right Side - Form */
.right-side {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #ffffff;
}

@media (min-width: 1024px) {
  .right-side {
    flex: 0 0 480px;
  }
}

.form-wrapper {
  width: 100%;
  max-width: 420px;
}

/* Login Header */
.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.user-icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
}

.user-icon {
  width: 40px;
  height: 40px;
  color: white;
}

.login-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.login-subtitle {
  color: #64748b;
  font-size: 0.875rem;
}

/* Alert Error */
.alert-error {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.875rem;
}

.alert-error svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

/* Method Toggle */
.method-toggle {
  display: flex;
  gap: 0.5rem;
  padding: 0.25rem;
  background: #f1f5f9;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.method-btn {
  flex: 1;
  padding: 0.75rem;
  border: none;
  background: transparent;
  color: #64748b;
  font-weight: 500;
  font-size: 0.875rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.method-btn.active {
  background: white;
  color: #667eea;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Form Container */
.form-container {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.input-wrapper {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #9ca3af;
  pointer-events: none;
}

.form-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s;
  outline: none;
}

.form-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.password-input {
  padding-right: 3rem;
}

.password-toggle {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #9ca3af;
  transition: color 0.2s;
}

.password-toggle:hover {
  color: #667eea;
}

.password-toggle svg {
  width: 20px;
  height: 20px;
}

/* OTP Container */
.otp-container {
  display: flex;
  gap: 0.5rem;
}

.otp-input {
  flex: 1;
  padding: 0.875rem 1rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s;
  outline: none;
}

.otp-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.otp-button {
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, #f36c21, #ffd54f);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.otp-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.otp-button.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Error & Info Messages */
.error-message {
  color: #dc2626;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.info-message {
  color: #667eea;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

/* Remember Section */
.remember-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
}

.remember-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox {
  width: 1rem;
  height: 1rem;
  accent-color: #667eea;
  cursor: pointer;
}

.checkbox-label {
  color: #64748b;
}

.forgot-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.forgot-link:hover {
  color: #5568d3;
}

/* Password Strength Indicator */
.password-strength {
  margin-top: 0.5rem;
}

.strength-bar {
  height: 4px;
  border-radius: 2px;
  margin-bottom: 0.25rem;
  transition: all 0.3s;
}

.strength-bar.weak {
  width: 33%;
  background-color: #ef4444;
}

.strength-bar.medium {
  width: 66%;
  background-color: #f59e0b;
}

.strength-bar.strong {
  width: 100%;
  background-color: #10b981;
}

.strength-text {
  font-size: 0.75rem;
  font-weight: 500;
}

.strength-bar.weak + .strength-text {
  color: #ef4444;
}

.strength-bar.medium + .strength-text {
  color: #f59e0b;
}

.strength-bar.strong + .strength-text {
  color: #10b981;
}

/* Submit Button */
.submit-btn {
  width: 100%;
  padding: 1rem;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  background: linear-gradient(135deg, #f36c21, #ffd54f);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Divider */
.divider {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1.5rem 0;
  justify-content: center;
}

.divider-line {
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

.divider-text {
  color: #9ca3af;
  font-size: 0.875rem;
}

/* Register Section */
.register-section {
  text-align: center;
}

.register-text {
  color: #64748b;
  font-size: 0.875rem;
}

.register-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}

.register-link:hover {
  color: #5568d3;
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 1023px) {
  .right-side {
    min-height: 100vh;
  }

  .form-wrapper {
    padding: 1rem;
  }
}
</style>
